import { useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { UNCATEGORIZED_ID } from "../../utils/financeCategoryFilter";

const FALLBACK_COLORS = [
  "#1976d2",
  "#c62828",
  "#2e7d32",
  "#6a1b9a",
  "#ef6c00",
  "#00838f",
  "#ad1457",
  "#4527a0",
  "#558b2f",
  "#00695c",
];

function expenseOf(cat) {
  return Math.abs(Math.min(0, Number(cat.amount || 0)));
}

function parseHex(color) {
  const raw = String(color || "").replace("#", "");
  if (raw.length !== 6) return null;
  const n = Number.parseInt(raw, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mixHex(hex, toward, ratio) {
  const a = parseHex(hex);
  const b = parseHex(toward);
  if (!a || !b) return hex || toward;
  const t = Math.max(0, Math.min(1, ratio));
  const ch = (from, to) => Math.round(from + (to - from) * t);
  return `#${[ch(a.r, b.r), ch(a.g, b.g), ch(a.b, b.b)]
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("")}`;
}

function segmentColor(parentColor, ownColor, index, count) {
  if (ownColor) return ownColor;
  const base = parentColor || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  if (count <= 1) return base;
  const toward = index % 2 === 0 ? "#ffffff" : "#111111";
  return mixHex(base, toward, 0.12 + (index / Math.max(count - 1, 1)) * 0.28);
}

function buildRows(summary) {
  const rows = (summary?.categories || [])
    .map((cat, index) => {
      const expense = expenseOf(cat);
      const children = (cat.children || [])
        .map((child) => ({ ...child, expense: expenseOf(child) }))
        .filter((child) => child.expense > 0)
        .sort((a, b) => b.expense - a.expense);
      const childSum = children.reduce((sum, child) => sum + child.expense, 0);
      const remainder = Math.max(0, Math.round((expense - childSum) * 100) / 100);
      const parentColor = cat.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
      const parts = [...children];
      if (remainder > 0.005) {
        parts.push({
          category_uuid: `${cat.category_uuid}::direct`,
          source_uuid: cat.category_uuid,
          name: children.length ? `${cat.name} (direkte)` : cat.name,
          expense: remainder,
          color: parentColor,
          isRemainder: children.length > 0,
        });
      }
      const segmentCount = parts.length;
      const segments = parts.map((part, partIndex) => ({
        ...part,
        color: segmentColor(parentColor, part.color, partIndex, segmentCount),
      }));
      return {
        category_uuid: String(cat.category_uuid),
        name: cat.name,
        color: parentColor,
        expense,
        segments,
        children,
      };
    })
    .filter((row) => row.expense > 0)
    .sort((a, b) => b.expense - a.expense);

  const uncategorizedExpense = expenseOf({ amount: summary?.uncategorized_amount });
  if (uncategorizedExpense > 0) {
    rows.push({
      category_uuid: UNCATEGORIZED_ID,
      name: "Ukategoriseret",
      color: "#9e9e9e",
      expense: uncategorizedExpense,
      segments: [
        {
          category_uuid: UNCATEGORIZED_ID,
          name: "Ukategoriseret",
          expense: uncategorizedExpense,
          color: "#9e9e9e",
        },
      ],
      children: [],
    });
  }
  return rows;
}

export default function FinanceCategoryBreakdown({
  summary,
  formatMoney,
  selectedIds = [],
}) {
  const rows = useMemo(() => buildRows(summary), [summary]);
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);

  const visibleRows = useMemo(() => {
    return rows
      .map((row) => {
        if (selectedSet.size === 0) return row;
        const parentOn = selectedSet.has(row.category_uuid);
        const segments = parentOn
          ? row.segments
          : row.segments.filter((seg) =>
              selectedSet.has(String(seg.source_uuid || seg.category_uuid))
            );
        if (!segments.length) return null;
        const expense = segments.reduce((sum, seg) => sum + seg.expense, 0);
        return { ...row, segments, expense };
      })
      .filter(Boolean);
  }, [rows, selectedSet]);

  const maxExpense = Math.max(1, ...visibleRows.map((row) => row.expense));

  if (!visibleRows.length) {
    return (
      <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Fordelt på kategorier
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Ingen udgifter i perioden.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Fordelt på kategorier
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Hold musen over farverne for underkategorier.
      </Typography>

      {visibleRows.map((row) => (
        <Box key={row.category_uuid} sx={{ mt: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="body2">{formatMoney(row.expense)}</Typography>
          </Stack>
          <Box
            sx={{
              width: `${Math.min(100, (row.expense / maxExpense) * 100)}%`,
              minWidth: 8,
            }}
          >
            <Box
              sx={{
                display: "flex",
                height: 10,
                bgcolor: "action.hover",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {row.segments.map((seg) => (
                <Tooltip
                  key={seg.category_uuid}
                  title={`${seg.name}: ${formatMoney(seg.expense)}`}
                  arrow
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.max(0, (seg.expense / Math.max(row.expense, 0.01)) * 100)}%`,
                      minWidth: 2,
                      bgcolor: seg.color || row.color || "primary.main",
                      "&:hover": { filter: "brightness(0.88)" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
